import React, { useEffect, useState } from 'react'
import { dedupeResponseRecords } from '../lib/offlineSurveyQueue'
import { useNavigate } from 'react-router-dom'
import { ClipboardList, CheckCircle, Clock, AlertCircle, PlayCircle, RefreshCw, Send, WifiOff, Loader2 } from 'lucide-react'
import toast from 'react-hot-toast'
import { lumi } from '../lib/lumi'
import ResearcherSurveyModal, { ResearcherSurvey } from '../components/ResearcherSurveyModal'
import { ensureHtmlString, stripHtml } from '../lib/surveyPresentation'
import {
  cacheResearcherAssignments,
  cacheSurvey,
  getCachedResearcherAssignments,
  getCachedSurvey,
  getOfflineQueueCount,
  syncOfflineSubmissions,
} from '../lib/offlineSurveyQueue'

type UserProfile = 'admin' | 'researcher'

interface AppSession {
  profile: UserProfile
  userId: string
  email: string
  userName: string
  userRole?: string
  researcherId?: string | null
  researcherName?: string | null
  loginAt: string
}

interface SurveyAssignment {
  _id: string
  surveyId: string
  researcherId: string
  status: 'assigned' | 'in_progress' | 'completed' | 'removed'
  assignedAt: string
  startedAt?: string
  completedAt?: string
  createdAt?: string
  updatedAt?: string
}

interface AssignmentWithSurvey extends SurveyAssignment {
  survey?: ResearcherSurvey
  responseCount?: number
}

interface InterviewResponse {
  surveyId?: string
  status?: 'completed' | 'cancelled' | 'draft'
  cancellationReason?: string
  submittedAt?: string
  completedAt?: string
  cancelledAt?: string
  createdAt?: string
  isDeleted?: boolean
  deletedAt?: string
}

type SurveyWithAltId = ResearcherSurvey & {
  id?: string
  status?: string
  isDeleted?: boolean | string
  deletedAt?: string
  deleted_at?: string
  removedAssignmentIds?: unknown
}

const getRemovedAssignmentIds = (survey?: ResearcherSurvey): string[] => {
  const raw = (survey as SurveyWithAltId | undefined)?.removedAssignmentIds
  return Array.isArray(raw) ? raw.filter((id): id is string => typeof id === 'string') : []
}

const SESSION_KEY = 'app_dna_session'

const readSession = (): AppSession | null => {
  if (typeof window === 'undefined') return null
  try {
    const raw = window.localStorage.getItem(SESSION_KEY)
    if (!raw) return null
    const session = JSON.parse(raw) as AppSession
    const valid = session.profile === 'admin' || session.profile === 'researcher'
    if (!valid || !session.userId || !session.email || !session.userName) return null
    return session
  } catch {
    return null
  }
}

const statusConfig = {
  assigned: { label: 'Atribuída', color: 'bg-blue-100 text-blue-700', icon: AlertCircle },
  in_progress: { label: 'Em Andamento', color: 'bg-yellow-100 text-yellow-700', icon: Clock },
  completed: { label: 'Concluída', color: 'bg-green-100 text-green-700', icon: CheckCircle },
}

const formatDate = (iso?: string) => {
  if (!iso) return '-'
  return new Date(iso).toLocaleDateString('pt-BR', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
  })
}

const getSurveyRecordIds = (survey?: SurveyWithAltId) => {
  if (!survey) return undefined
  return [survey._id, survey.id].filter((value): value is string => typeof value === 'string' && value.trim().length > 0)
}

const surveyMatchesId = (survey: SurveyWithAltId | undefined, surveyId: string) => {
  const recordIds = getSurveyRecordIds(survey)
  return !!recordIds && recordIds.includes(surveyId)
}

const isSurveyVisible = (survey?: SurveyWithAltId) => {
  if (!survey) return false

  const isDeletedFlag = survey.isDeleted === true || String(survey.isDeleted).toLowerCase() === 'true'
  const hasDeletedAt = !!(survey.deletedAt || survey.deleted_at)
  const deletedStatus = ['deleted', 'removed', 'excluida', 'excluido'].includes(String(survey.status || '').toLowerCase())

  return !isDeletedFlag && !hasDeletedAt && !deletedStatus
}

const ResearcherDashboard: React.FC = () => {
  const navigate = useNavigate()
  const session = readSession()

  const [assignments, setAssignments] = useState<AssignmentWithSurvey[]>([])
  const [loading, setLoading] = useState(true)
  // Evita cargas simultaneas e guarda a "assinatura" das atribuicoes para o
  // pulso leve detectar novidades (nova pesquisa do admin, mudanca de status).
  const dashboardLoadRef = React.useRef(false)
  const assignmentsSignatureRef = React.useRef('')
  const [activeSurvey, setActiveSurvey] = useState<ResearcherSurvey | null>(null)
  const [loadingSurveyId, setLoadingSurveyId] = useState<string | null>(null)
  const [offlineTotal, setOfflineTotal] = useState(0)
  const [isSyncingAll, setIsSyncingAll] = useState(false)
  const [surveySessionKey, setSurveySessionKey] = useState(0)
  const [interviewResponses, setInterviewResponses] = useState<InterviewResponse[]>([])
  const [counterSurveyId, setCounterSurveyId] = useState('')
  const [counterStartDate, setCounterStartDate] = useState('')
  const [counterEndDate, setCounterEndDate] = useState('')

  useEffect(() => {
    if (!session || session.profile !== 'researcher' || !session.researcherId) {
      navigate('/login', { replace: true })
    }
  }, [])

  useEffect(() => {
    const updateOfflineCount = () => {
      const count = getOfflineQueueCount({ researcherId: session?.researcherId || undefined })
      setOfflineTotal(count)
    }
    updateOfflineCount()

    const handleQueueChange = () => updateOfflineCount()
    const handleOnline = async () => {
      updateOfflineCount()
      const count = getOfflineQueueCount({ researcherId: session?.researcherId || undefined })
      if (count > 0 && session?.researcherId) {
        try {
          const res = await syncOfflineSubmissions({ researcherId: session.researcherId })
          if (res.sent > 0) {
            toast.success(`${res.sent} resposta(s) sincronizada(s) com sucesso!`)
            fetchAssignments()
          }
        } catch { /* ignore */ }
        updateOfflineCount()
      }
    }

    window.addEventListener('dna_offline_queue_changed', handleQueueChange)
    window.addEventListener('online', handleOnline)
    return () => {
      window.removeEventListener('dna_offline_queue_changed', handleQueueChange)
      window.removeEventListener('online', handleOnline)
    }
  }, [session?.researcherId])

  const findSurveyById = async (
    surveyId: string,
    fallbackSurvey?: ResearcherSurvey
  ): Promise<ResearcherSurvey | undefined> => {
    if (fallbackSurvey && surveyMatchesId(fallbackSurvey as SurveyWithAltId, surveyId) && isSurveyVisible(fallbackSurvey as SurveyWithAltId)) {
      return fallbackSurvey
    }

    const cached = getCachedSurvey(surveyId)
    if (cached && isSurveyVisible(cached as SurveyWithAltId)) {
      return cached
    }

    if (!navigator.onLine) {
      return fallbackSurvey || cached
    }

    try {
      const byId = await lumi.entities.surveys.get(surveyId) as ResearcherSurvey | undefined
      if (surveyMatchesId(byId as SurveyWithAltId, surveyId) && isSurveyVisible(byId as SurveyWithAltId)) {
        cacheSurvey(byId)
        return byId
      }
    } catch {
      // fallback below
    }

    for (const filter of [{ _id: surveyId }, { id: surveyId }]) {
      try {
        const byFilter = await lumi.entities.surveys.list({
          filter,
          limit: 1,
        }) as { list?: ResearcherSurvey[] }

        const filteredSurvey = byFilter.list?.find((item) => surveyMatchesId(item as SurveyWithAltId, surveyId))
        if (isSurveyVisible(filteredSurvey as SurveyWithAltId)) {
          cacheSurvey(filteredSurvey)
          return filteredSurvey
        }
      } catch {
        // fallback below
      }
    }

    try {
      const allSurveys = await lumi.entities.surveys.list({
        limit: 1000,
      }) as { list?: ResearcherSurvey[] }

      const found = (allSurveys.list || []).find((survey) => {
        return surveyMatchesId(survey as SurveyWithAltId, surveyId) && isSurveyVisible(survey as SurveyWithAltId)
      })
      if (found) cacheSurvey(found)
      return found || fallbackSurvey || cached
    } catch {
      return fallbackSurvey || cached
    }
  }

  const fetchAssignments = async () => {
    if (!session?.researcherId) return
    if (dashboardLoadRef.current) return
    dashboardLoadRef.current = true
    setLoading(true)

    if (!navigator.onLine) {
      const cachedAssignments = getCachedResearcherAssignments(session.researcherId)
      if (cachedAssignments && cachedAssignments.length > 0) {
        setAssignments(cachedAssignments)
        setLoading(false)
        dashboardLoadRef.current = false
        return
      }
    }

    try {
      const { list: assignmentList } = await lumi.entities.survey_assignments.list({
        filter: { researcherId: session.researcherId },
        sort: { assignedAt: -1 },
        limit: 5000,
      }) as { list: SurveyAssignment[] }

      // Assinatura das atribuicoes: base de comparacao do pulso leve.
      assignmentsSignatureRef.current = (assignmentList || [])
        .filter((assignment) => assignment.status !== 'removed')
        .map((assignment) => `${assignment._id}:${assignment.status}`)
        .sort()
        .join('|')

      const [responsesResult, surveysResult] = await Promise.all([
        lumi.entities.responses.list({
          filter: { researcherId: session.researcherId },
          limit: 5000,
        }).catch(() => ({ list: [] as Array<{ surveyId?: string }> })),
        lumi.entities.surveys.list({
          limit: 5000,
        }).catch(() => ({ list: [] as ResearcherSurvey[] })),
      ]) as [{ list?: InterviewResponse[] }, { list?: ResearcherSurvey[] }]

      const uniqueResponses = dedupeResponseRecords(responsesResult.list || [])

      setInterviewResponses(uniqueResponses.filter((response) => !response.isDeleted && !response.deletedAt))

      const responseCountMap = new Map<string, number>()
      uniqueResponses.forEach((response) => {
        if (!response.surveyId || response.isDeleted || response.deletedAt) return
        responseCountMap.set(response.surveyId, (responseCountMap.get(response.surveyId) || 0) + 1)
      })

      const removedAssignmentIds = new Set<string>()
      ;(surveysResult.list || []).forEach((survey) => {
        getRemovedAssignmentIds(survey).forEach((id) => removedAssignmentIds.add(id))
      })

      const visibleAssignmentList = (assignmentList || []).filter((assignment) => (
        assignment.status !== 'removed' && !removedAssignmentIds.has(assignment._id)
      ))

      if (visibleAssignmentList.length === 0) {
        setAssignments([])
        cacheResearcherAssignments(session.researcherId, [])
        return
      }

      const surveyIds = [...new Set(visibleAssignmentList.map((assignment) => assignment.surveyId))]
      const surveyMap: Record<string, ResearcherSurvey> = {}

      ;(surveysResult.list || [])
        .filter((survey) => isSurveyVisible(survey as SurveyWithAltId))
        .forEach((survey) => {
          cacheSurvey(survey)
          const recordIds = getSurveyRecordIds(survey as SurveyWithAltId) || []
          recordIds.forEach((recordId) => {
            surveyMap[recordId] = survey
          })
        })

      const missingSurveyIds = surveyIds.filter((surveyId) => !surveyMap[surveyId])
      const fallbackSurveyResults = await Promise.all(
        missingSurveyIds.map(async (surveyId) => {
          const survey = await findSurveyById(surveyId)
          return survey ? [survey] : []
        })
      )

      fallbackSurveyResults.flat().forEach((survey) => {
        cacheSurvey(survey)
        const recordIds = getSurveyRecordIds(survey as SurveyWithAltId) || []
        recordIds.forEach((recordId) => {
          surveyMap[recordId] = survey
        })
      })

      const assignmentsWithSurveys = visibleAssignmentList
        .map((assignment) => ({
          ...assignment,
          survey: surveyMap[assignment.surveyId] || getCachedSurvey(assignment.surveyId),
          responseCount: responseCountMap.get(assignment.surveyId) || 0,
        }))
        .filter((assignment) => isSurveyVisible(assignment.survey as SurveyWithAltId))

      setAssignments(assignmentsWithSurveys)
      cacheResearcherAssignments(session.researcherId, assignmentsWithSurveys)
    } catch (err) {
      console.error('Erro ao carregar atribuições:', err)
      const cached = getCachedResearcherAssignments(session.researcherId)
      if (cached && cached.length > 0) {
        setAssignments(cached)
        toast('Modo Offline: exibindo pesquisas salvas no dispositivo.', { icon: '📶' })
      } else {
        toast.error('Erro ao carregar pesquisas atribuídas.')
      }
    } finally {
      setLoading(false)
      dashboardLoadRef.current = false
    }
  }

  // Mantem o painel do pesquisador atualizado: ao voltar para a aba e
  // periodicamente, uma consulta LEVE de atribuicoes detecta novidades
  // (nova pesquisa atribuida, mudanca de status); havendo mudanca, o
  // painel inteiro e recarregado. Offline, nada e feito.
  useEffect(() => {
    const pollAssignmentsLight = async () => {
      if (!session?.researcherId) return
      if (!navigator.onLine) return
      if (document.visibilityState !== 'visible') return
      if (dashboardLoadRef.current) return
      try {
        const { list: assignmentList } = await lumi.entities.survey_assignments.list({
          filter: { researcherId: session.researcherId },
          sort: { assignedAt: -1 },
          limit: 5000,
        }) as { list: SurveyAssignment[] }

        const signature = (assignmentList || [])
          .filter((assignment) => assignment.status !== 'removed')
          .map((assignment) => `${assignment._id}:${assignment.status}`)
          .sort()
          .join('|')

        if (signature !== assignmentsSignatureRef.current) {
          void fetchAssignments()
        }
      } catch { /* silencioso: o proximo ciclo tenta de novo */ }
    }

    const interval = window.setInterval(pollAssignmentsLight, 30000)
    const refreshWhenVisible = () => { void pollAssignmentsLight() }
    window.addEventListener('focus', refreshWhenVisible)
    document.addEventListener('visibilitychange', refreshWhenVisible)
    return () => {
      window.clearInterval(interval)
      window.removeEventListener('focus', refreshWhenVisible)
      document.removeEventListener('visibilitychange', refreshWhenVisible)
    }
  }, [])

  const handleStartCollection = async (assignment: AssignmentWithSurvey) => {
    const surveyId = assignment.surveyId
    setLoadingSurveyId(surveyId)
    try {
      if (assignment.status !== 'in_progress' && session?.researcherId && navigator.onLine) {
        try {
          await lumi.entities.survey_assignments.update(assignment._id, {
            status: 'in_progress',
            startedAt: new Date().toISOString(),
            updatedAt: new Date().toISOString(),
          })
        } catch {
          // nao bloqueia a abertura do modal
        }
      }

      const fullSurvey = await findSurveyById(surveyId, assignment.survey)

      if (!fullSurvey || !isSurveyVisible(fullSurvey as SurveyWithAltId)) {
        setAssignments((prev) => prev.filter((item) => item._id !== assignment._id))
        toast.error('Esta pesquisa não está disponível no momento.')
        return
      }

      setAssignments((prev) => prev.map((item) =>
        item._id === assignment._id && item.status !== 'in_progress'
          ? { ...item, status: 'in_progress', startedAt: new Date().toISOString(), survey: fullSurvey }
          : { ...item, survey: item.surveyId === surveyId ? item.survey || fullSurvey : item.survey }
      ))

      setSurveySessionKey((prev) => prev + 1)
      setActiveSurvey(fullSurvey)
    } catch (err) {
      console.error('Erro ao abrir pesquisa:', err)
      toast.error('Erro ao abrir a pesquisa.')
    } finally {
      setLoadingSurveyId(null)
    }
  }

  const handleSyncAllOffline = async () => {
    if (!navigator.onLine) {
      toast.error('Ainda sem conexão com a internet. Conecte-se ao Wi-Fi ou 4G para sincronizar.')
      return
    }
    setIsSyncingAll(true)
    try {
      const res = await syncOfflineSubmissions({ researcherId: session?.researcherId || undefined })
      if (res.sent > 0) {
        toast.success(`${res.sent} resposta(s) offline sincronizada(s) com sucesso!`)
        fetchAssignments()
      } else if (res.failed > 0) {
        toast.error('Algumas respostas não puderam ser enviadas. Tente novamente.')
      } else {
        toast('Nenhuma resposta pendente para sincronizar.', { icon: 'ℹ️' })
      }
    } catch (err) {
      toast.error('Falha ao sincronizar respostas.')
    } finally {
      setIsSyncingAll(false)
      setOfflineTotal(getOfflineQueueCount({ researcherId: session?.researcherId || undefined }))
    }
  }

  const handleModalClose = () => setActiveSurvey(null)

  const handleModalSubmitted = () => {
    setActiveSurvey(null)
    fetchAssignments()
  }

  const filteredResponses = interviewResponses.filter((response) => {
    if (counterSurveyId && response.surveyId !== counterSurveyId) return false
    const date = response.submittedAt || response.completedAt || response.cancelledAt || response.createdAt || ''
    if (counterStartDate && date.slice(0, 10) < counterStartDate) return false
    if (counterEndDate && date.slice(0, 10) > counterEndDate) return false
    return true
  })
  const approved = filteredResponses.filter((response) => response.status === 'completed').length
  const filteredOut = filteredResponses.filter((response) => response.status === 'cancelled' && response.cancellationReason === 'filter').length
  const cancelled = filteredResponses.filter((response) => response.status === 'cancelled' && response.cancellationReason !== 'filter').length
  const total = assignments.length
  const inProgress = assignments.filter((assignment) => assignment.status === 'in_progress').length
  const completed = approved
  const pending = assignments.filter((assignment) => assignment.status === 'assigned').length

  return (
    <>
      <div className="space-y-6">
        {/* Banner de Sincronização Offline */}
        {offlineTotal > 0 && (
          <div className="rounded-2xl border border-amber-300 bg-amber-50 p-4 shadow-sm">
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
              <div className="flex items-center gap-3">
                <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-amber-100 text-amber-700">
                  <WifiOff size={20} />
                </div>
                <div>
                  <h3 className="text-sm font-bold text-amber-900">
                    📦 {offlineTotal} resposta(s) coletada(s) offline neste aparelho
                  </h3>
                  <p className="text-xs text-amber-700 mt-0.5">
                    As entrevistas foram salvas localmente e aguardam conexão para subir ao servidor.
                  </p>
                </div>
              </div>

              <button
                type="button"
                disabled={isSyncingAll}
                onClick={handleSyncAllOffline}
                className="inline-flex items-center justify-center gap-2 rounded-lg bg-amber-600 px-4 py-2 text-xs font-bold text-white shadow-sm transition-colors hover:bg-amber-700 disabled:opacity-50"
              >
                {isSyncingAll ? (
                  <>
                    <Loader2 size={14} className="animate-spin" />
                    <span>Sincronizando...</span>
                  </>
                ) : (
                  <>
                    <Send size={14} />
                    <span>Sincronizar Tudo ({offlineTotal})</span>
                  </>
                )}
              </button>
            </div>
          </div>
        )}

        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold text-gray-900">
              Olá, {session?.researcherName || session?.userName}
            </h1>
            <p className="text-sm text-gray-500 mt-1">
              Suas pesquisas atribuídas estão listadas abaixo.
            </p>
          </div>
          <button
            onClick={fetchAssignments}
            disabled={loading}
            className="flex items-center gap-2 px-4 py-2 text-sm font-medium text-gray-600 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors disabled:opacity-50"
          >
            <RefreshCw size={16} className={loading ? 'animate-spin' : ''} />
            Atualizar
          </button>
        </div>

        <div className="rounded-xl border border-gray-200 bg-white p-4">
          <div className="grid grid-cols-1 gap-3 md:grid-cols-3">
            <select value={counterSurveyId} onChange={(event) => setCounterSurveyId(event.target.value)} className="rounded-lg border border-gray-300 px-3 py-2 text-sm"><option value="">Todas as pesquisas</option>{assignments.map((assignment) => <option key={assignment._id} value={assignment.surveyId}>{stripHtml(assignment.survey?.title || assignment.surveyId)}</option>)}</select>
            <input type="date" value={counterStartDate} onChange={(event) => setCounterStartDate(event.target.value)} className="rounded-lg border border-gray-300 px-3 py-2 text-sm" aria-label="Data inicial" />
            <input type="date" value={counterEndDate} onChange={(event) => setCounterEndDate(event.target.value)} className="rounded-lg border border-gray-300 px-3 py-2 text-sm" aria-label="Data final" />
          </div>
        </div>

        <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
          {[
            { label: 'Total Atribuídas', value: total, color: 'text-blue-600', bg: 'bg-blue-50', icon: ClipboardList },
            { label: 'Pendentes', value: pending, color: 'text-orange-600', bg: 'bg-orange-50', icon: AlertCircle },
            { label: 'Em Andamento', value: inProgress, color: 'text-yellow-600', bg: 'bg-yellow-50', icon: Clock },
            { label: 'Concluídas', value: completed, color: 'text-green-600', bg: 'bg-green-50', icon: CheckCircle },
            { label: 'Canceladas por filtro', value: filteredOut, color: 'text-purple-600', bg: 'bg-purple-50', icon: AlertCircle },
            { label: 'Canceladas', value: cancelled, color: 'text-red-600', bg: 'bg-red-50', icon: AlertCircle },
          ].map((stat) => {
            const Icon = stat.icon
            return (
              <div key={stat.label} className="bg-white rounded-xl border border-gray-200 p-4 flex items-center gap-3">
                <div className={`${stat.bg} p-2 rounded-lg`}>
                  <Icon size={20} className={stat.color} />
                </div>
                <div>
                  <p className={`text-2xl font-bold ${stat.color}`}>{stat.value}</p>
                  <p className="text-xs text-gray-500">{stat.label}</p>
                </div>
              </div>
            )
          })}
        </div>

        <div className="bg-white rounded-xl border border-gray-200">
          <div className="px-6 py-4 border-b border-gray-100">
            <h2 className="text-base font-semibold text-gray-800">Pesquisas Atribuídas</h2>
          </div>

          {loading ? (
            <div className="flex items-center justify-center py-16">
              <RefreshCw size={24} className="animate-spin text-blue-500 mr-3" />
              <span className="text-gray-500">Carregando pesquisas...</span>
            </div>
          ) : assignments.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-16 text-center">
              <ClipboardList size={40} className="text-gray-300 mb-3" />
              <p className="text-gray-500 font-medium">Nenhuma pesquisa atribuída</p>
              <p className="text-sm text-gray-400 mt-1">
                Quando um administrador atribuir uma pesquisa a você, ela aparecerá aqui.
              </p>
            </div>
          ) : (
            <ul className="divide-y divide-gray-100">
              {assignments.map((assignment) => {
                const cfg = statusConfig[assignment.status] || statusConfig.assigned
                const StatusIcon = cfg.icon
                const surveyTitle = ensureHtmlString(assignment.survey?.title || `Pesquisa #${assignment.surveyId.slice(-6)}`)
                const isLoadingThis = loadingSurveyId === assignment.surveyId
                const actionLabel =
                  assignment.status === 'in_progress'
                    ? (assignment.responseCount || 0) > 0 ? 'Nova resposta' : 'Continuar'
                    : assignment.status === 'completed'
                      ? 'Nova resposta'
                      : 'Iniciar'

                return (
                  <li
                    key={assignment._id}
                    className="px-6 py-4 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 hover:bg-gray-50 transition-colors"
                  >
                    <div className="flex-1 min-w-0">
                      <div className="flex items-start gap-2 flex-wrap">
                        <h3
                          className="min-w-0 break-words font-semibold text-gray-900 [overflow-wrap:anywhere] [&_*]:max-w-full"
                          dangerouslySetInnerHTML={{ __html: surveyTitle }}
                        />
                        <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium ${cfg.color}`}>
                          <StatusIcon size={12} />
                          {cfg.label}
                        </span>
                      </div>
                      <div className="mt-1 flex flex-wrap gap-x-4 text-xs text-gray-400">
                        <span>Atribuída em: {formatDate(assignment.assignedAt)}</span>
                        {assignment.startedAt && <span>Iniciada em: {formatDate(assignment.startedAt)}</span>}
                        {assignment.completedAt && <span>Concluída em: {formatDate(assignment.completedAt)}</span>}
                        <span>Respondida(s): {assignment.responseCount || 0}</span>
                      </div>
                      {assignment.survey?.description && (
                        <p className="mt-1 text-xs text-gray-400 line-clamp-1">
                          {stripHtml(assignment.survey.description)}
                        </p>
                      )}
                    </div>

                    <button
                      onClick={() => handleStartCollection(assignment)}
                      disabled={isLoadingThis || !!loadingSurveyId}
                      className="flex items-center gap-2 px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-lg hover:bg-blue-700 transition-colors shrink-0 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      {isLoadingThis ? (
                        <RefreshCw size={15} className="animate-spin" />
                      ) : (
                        <PlayCircle size={16} />
                      )}
                      {actionLabel}
                    </button>
                  </li>
                )
              })}
            </ul>
          )}
        </div>
      </div>

      {activeSurvey && session?.researcherId && (
        <ResearcherSurveyModal
          key={`${activeSurvey._id || (activeSurvey as any).id || 'survey'}_${surveySessionKey}`}
          survey={activeSurvey}
          researcherId={session.researcherId}
          researcherName={session.researcherName || session.userName}
          onClose={handleModalClose}
          onSubmitted={handleModalSubmitted}
        />
      )}
    </>
  )
}

export default ResearcherDashboard